import { List, Empty, Button, Popconfirm, message } from 'antd';
import type { ConferenceRef } from '@viq-admin/types';
import { SectionHeader } from '@viq-admin/ui';
import { useAbility } from '../../ability/AbilityContext';
import { bulkUnassign } from '../../api/conferences';
import { ConferenceLink } from '../../components/EntityLinks';

interface Props {
  oid: string;
  userCompanyId: string;
  conferences: ConferenceRef[];
  onChanged: () => void;
}

/**
 * Conferences panel for the user page: the conferences this user is assigned
 * to (from the user record, GET /v1/superadmin/users/{oid}), each name a link
 * to that conference's page, with a per-row Remove (parity with the portal —
 * the console side used to be read-only). Remove is gated on `manage
 * Conference` (the same cap the conference Assignments tab uses) and unassigns
 * the single (user, conference) pair through the existing bulk endpoint with a
 * one-element payload. Renders an empty state when the user has no
 * conferences.
 */
export function ConferencesPanel({
  oid,
  userCompanyId,
  conferences,
  onChanged,
}: Props) {
  const ability = useAbility();
  const canManage = ability.can('manage', 'Conference');

  async function remove(confId: string, confName: string) {
    try {
      await bulkUnassign(confId, userCompanyId, [oid]);
      message.success(`Removed ${confName}.`);
      onChanged();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Remove failed');
    }
  }

  return (
    <div>
      <SectionHeader>Conferences</SectionHeader>
      {conferences.length === 0 ? (
        <Empty
          description="No conferences assigned"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          size="small"
          dataSource={conferences}
          renderItem={(c) => (
            <List.Item
              key={c.id}
              actions={
                canManage
                  ? [
                      <Popconfirm
                        key="remove"
                        title={`Remove ${c.name} from this user?`}
                        onConfirm={() => remove(c.id, c.name)}
                      >
                        <Button danger size="small" type="link">
                          Remove
                        </Button>
                      </Popconfirm>,
                    ]
                  : undefined
              }
            >
              <ConferenceLink id={c.id} name={c.name} />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
